import { Activity, Clock3, Coins, Cpu, Gauge } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@components/ui/Badge'
import type { RecipeExecutionUsageSummary } from '@/services/api/recipes'

interface RecipeExecutionUsageSummaryCardProps {
  summary?: RecipeExecutionUsageSummary | null
}

function formatDuration(ms?: number) {
  if (!ms || ms <= 0) return null
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function formatNumber(value?: number) {
  return new Intl.NumberFormat().format(value ?? 0)
}

function formatCost(value?: number) {
  if (!value || value <= 0) return '$0.0000'
  return `$${value.toFixed(value < 0.01 ? 6 : 4)}`
}

function sourceLabel(summary: RecipeExecutionUsageSummary, t: (key: string) => string) {
  if (summary.source === 'metering_clickhouse') return t('historyDetail.usageSourceMetering')
  if (summary.metering?.status === 'not_found') return t('historyDetail.usageSourceTrace')
  return t('historyDetail.usageSourceTrace')
}

export default function RecipeExecutionUsageSummaryCard({
  summary,
}: RecipeExecutionUsageSummaryCardProps) {
  const { t } = useTranslation()

  if (!summary) {
    return (
      <section className="rounded-lg border border-border bg-muted/20 px-3 py-3 text-sm text-muted-foreground">
        {t('historyDetail.usageNoData')}
      </section>
    )
  }

  const duration = formatDuration(summary.duration_ms)
  const topModels = summary.llm.by_model?.slice(0, 3) ?? []

  return (
    <section className="space-y-3 rounded-lg border border-border bg-muted/20 px-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Gauge className="h-4 w-4 text-primary" />
          <h4 className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('historyDetail.usageTitle')}
          </h4>
        </div>
        <Badge variant="outline">{sourceLabel(summary, t)}</Badge>
      </div>

      <div className="grid gap-2 text-sm sm:grid-cols-2">
        <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock3 className="h-3.5 w-3.5" />
            {t('historyDetail.usageDuration')}
          </div>
          <div className="mt-1 font-medium">{duration ?? t('historyDetail.notAvailable')}</div>
        </div>
        <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Activity className="h-3.5 w-3.5" />
            {t('historyDetail.usageActions')}
          </div>
          <div className="mt-1 font-medium">
            {summary.actions.total}
            {summary.actions.failed > 0 ? ` / ${summary.actions.failed} ${t('historyDetail.usageFailures')}` : ''}
          </div>
        </div>
        <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Cpu className="h-3.5 w-3.5" />
            {t('historyDetail.usageTokens')}
          </div>
          <div className="mt-1 font-medium">{formatNumber(summary.llm.tokens_total)}</div>
        </div>
        <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Coins className="h-3.5 w-3.5" />
            {t('historyDetail.usageEstimatedCost')}
          </div>
          <div className="mt-1 font-medium">{formatCost(summary.cost.estimated_cost_usd)}</div>
        </div>
      </div>

      <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
        <div>
          {t('historyDetail.usageLLMCalls')}: <span className="font-medium text-foreground">{summary.llm.calls}</span>
        </div>
        <div>
          {summary.cost.monetary_cost
            ? `${summary.cost.monetary_cost.amount} ${summary.cost.monetary_cost.currency}`
            : t('historyDetail.usageMonetaryUnavailable')}
        </div>
      </div>

      {topModels.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">
            {t('historyDetail.usageModelBreakdown')}
          </div>
          {topModels.map((item) => (
            <div key={`${item.provider ?? ''}:${item.model ?? ''}`} className="flex items-center justify-between gap-3 rounded-md bg-background/70 px-2 py-1.5 text-xs">
              <span className="min-w-0 truncate">
                {[item.provider, item.model].filter(Boolean).join(' / ') || t('historyDetail.notAvailable')}
              </span>
              <span className="shrink-0 text-muted-foreground">
                {formatNumber(item.tokens_total)} · {formatCost(item.estimated_cost_usd)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
